/**
 * notificationBadge — F-SW7-15 (sweep7-e4-fe-4)
 *
 * Deterministisches Badge-Mapping direkt vom `event_type` der API
 * (GET /api/v2/notifications) — NICHT mehr über die alte, zufällig wirkende
 * `eventTypeToKind`-Umleitung auf die stub-basierten `KIND_CONFIG`-Label-Strings
 * aus notificationData.ts (dort war z.B. `witness -> 'commission'` und
 * `beacon -> 'speedtest'` hartkodiert — beides fachlich falsch, siehe Finding).
 *
 * Verifiziert gegen echte Backend-Daten (GET /api/v2/notifications,
 * demo_test@heliumtracker.io, :3927): event_type ∈ {reward, offline, online,
 * beacon, witness, heartbeat, payout, payment}. Für unbekannte/künftige
 * event_types greift ein neutraler Default (kein Crash, kein Roh-Enum).
 */
import type { ComponentType } from 'react'
import { Coins, WifiOff, Wifi, Radio, Eye, Activity, Wallet, CreditCard, Bell, Ban } from 'lucide-react'

export type NotificationBadgeConfig = {
  /**
   * Akzentfarbe (CSS-Var) für den Icon-Chip, exakt wie im Prototyp
   * (notification-log.html: `KIND`-Map mit `var(--success)` etc.) — bewusst
   * NICHT die geteilte `identity-img`/`tone-N`-Klasse (dort ist die
   * Icon-Farbe fest auf `color:white` gesetzt, was ein lucide-Icon auf den
   * hellen Tone-Hintergründen unlesbar macht; siehe Screenshot-Befund).
   */
  accent: string
  /** Pill-Klasse für das Badge. */
  pillClass: string
  /** i18n-Key (Namespace "notifications") für den Badge-Text. */
  labelKey: string
  /** i18n-Key (Namespace "notifications") für die volle, lokalisierte Zeilen-Überschrift. */
  titleKey: string
  /** Icon-Komponente für den Event-Typ. */
  icon: ComponentType<{ size?: number }>
}

// ACCEPTANCE (kind filters incl. ban + watched): 'denylist' (Bann-Engine,
// Story 3.2) und 'listen' (Guard-IP/Adress-Änderung, Notification::Listen)
// sind reale event_types aus GET /api/v2/notifications — bilden Ban/Watched
// ohne neuen Backend-Endpunkt ab.
const EVENT_TYPE_BADGE_MAP: Record<string, NotificationBadgeConfig> = {
  reward:    { accent: 'var(--success)', pillClass: 'pill-success', labelKey: 'eventBadge.reward',    titleKey: 'eventTitle.reward',    icon: Coins },
  offline:   { accent: 'var(--danger)',  pillClass: 'pill-danger',  labelKey: 'eventBadge.offline',   titleKey: 'eventTitle.offline',   icon: WifiOff },
  online:    { accent: 'var(--primary)', pillClass: 'pill-primary', labelKey: 'eventBadge.online',    titleKey: 'eventTitle.online',    icon: Wifi },
  heartbeat: { accent: 'var(--primary)', pillClass: 'pill-primary', labelKey: 'eventBadge.heartbeat', titleKey: 'eventTitle.heartbeat', icon: Activity },
  beacon:    { accent: 'var(--accent)',  pillClass: 'pill-info',    labelKey: 'eventBadge.beacon',    titleKey: 'eventTitle.beacon',    icon: Radio },
  witness:   { accent: 'var(--accent)',  pillClass: 'pill-info',    labelKey: 'eventBadge.witness',   titleKey: 'eventTitle.witness',   icon: Eye },
  payout:    { accent: 'var(--warning)', pillClass: 'pill-warning', labelKey: 'eventBadge.payout',    titleKey: 'eventTitle.payout',    icon: Wallet },
  payment:   { accent: 'var(--accent)',  pillClass: 'pill-accent',  labelKey: 'eventBadge.payment',   titleKey: 'eventTitle.payment',   icon: CreditCard },
  denylist:  { accent: 'var(--danger)',  pillClass: 'pill-danger',  labelKey: 'eventBadge.ban',       titleKey: 'eventTitle.ban',       icon: Ban },
  listen:    { accent: 'var(--accent)',  pillClass: 'pill-info',    labelKey: 'eventBadge.watched',   titleKey: 'eventTitle.watched',   icon: Eye },
}

const DEFAULT_BADGE_CONFIG: NotificationBadgeConfig = {
  accent: 'var(--text-muted)',
  pillClass: 'pill-primary',
  labelKey: 'eventBadge.default',
  titleKey: 'eventTitle.default',
  icon: Bell,
}

/** Liefert die Badge-Konfiguration (Icon/Farbe/i18n-Label) für einen event_type. */
export function notificationBadgeConfig(event_type: string): NotificationBadgeConfig {
  return EVENT_TYPE_BADGE_MAP[event_type] ?? DEFAULT_BADGE_CONFIG
}

/** Alle bekannten event_types — für tabellengetriebene Tests (TEA-Auflage). */
export const KNOWN_NOTIFICATION_EVENT_TYPES = Object.keys(EVENT_TYPE_BADGE_MAP)
